import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe, ChevronDown } from 'lucide-react';
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from '@/i18n';
import { cn } from '@/utils/cn';

const FLAGS: Record<SupportedLanguage, string> = {
  de: '🇩🇪',
  it: '🇮🇹',
  en: '🇬🇧',
};

const LABELS: Record<SupportedLanguage, string> = {
  de: 'Deutsch',
  it: 'Italiano',
  en: 'English',
};

/**
 * Sprach-Wechsler — wird im PublicHeader rechts angezeigt.
 * Persistiert die Wahl im localStorage via i18next LanguageDetector.
 */
export function LanguageSwitcher({ compact = false }: { compact?: boolean }) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const currentLng = (i18n.resolvedLanguage ?? i18n.language ?? 'de').slice(0, 2) as SupportedLanguage;

  const switchTo = (lng: SupportedLanguage) => {
    void i18n.changeLanguage(lng);
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      {/* 09.T1: min-h-11 (44px) Touch-Target */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'inline-flex items-center gap-1 rounded transition-colors min-h-11',
          compact ? 'px-2.5 text-sm' : 'px-3 text-sm',
          'hover:bg-paper-2 text-ink-2',
        )}
        aria-label="Sprache wählen"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <Globe className="size-3.5" />
        <span className="font-sans text-xs uppercase tracking-[0.18em] font-semibold">
          {currentLng}
        </span>
        <ChevronDown className={cn('size-3 transition-transform', open && 'rotate-180')} />
      </button>

      {open && (
        <ul
          role="listbox"
          className="absolute right-0 top-full mt-1 bg-paper border border-rule rounded-md shadow-md py-1 min-w-[140px] z-50"
        >
          {SUPPORTED_LANGUAGES.map((lng) => (
            <li key={lng}>
              <button
                type="button"
                onClick={() => switchTo(lng)}
                className={cn(
                  'w-full px-3 py-2.5 min-h-11 text-left inline-flex items-center gap-2 hover:bg-paper-2 transition-colors',
                  currentLng === lng && 'bg-gold/10',
                )}
                role="option"
                aria-selected={currentLng === lng}
              >
                <span className="text-base">{FLAGS[lng]}</span>
                <span className="font-sans text-sm text-ink-2">{LABELS[lng]}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
